import React, { useEffect, useRef, useState } from 'react'
import styles from './Pallets.module.css'
import ls from './Loads.module.css'
import o from '../../components/Overlay.module.css'
import sd from './SdPanel.module.css'
import { SdPanel } from './SdPanel'
import { apiJson } from '../../lib/api'
import { useLayer, useChanged } from '../../components/layers'
import { PendingButton, inertClass, useSlow } from '../../components/pending'
import { plural, type Pallet, type Ref } from './model'
import { dispatchBlock, joinableLoads, nowTime, type Load, type LoadOptions } from './loads'
import { todayIso } from './orders'

// Dispatch from the order (Adrian and Vanessa, 2026-11-02): from the order's
// Dispatches & documents tab, pick the carrier, untick any pallet not on the
// truck, and save. A new load is created, verified by sight and departs in
// one step (POST /api/orders/:id/dispatch); or, where an open load with that
// carrier departs today, the pallets can join it and it departs from its own
// record. The Dispatches menu stays for anyone who builds loads there.

const errText = (e: any) => e?.body?.error || e?.message || String(e)

const NEW = ''

export function DispatchPalletsPanel({
  user,
  orderId,
  orderRef,
  pallets,
  onCancel,
  onDone,
}: {
  user: any
  orderId: string
  orderRef: string
  // the order's live pallets (the record's), departed ones included
  pallets: Pallet[]
  onCancel: () => void
  onDone: (message: string) => void
}) {
  const shown = pallets.filter((p) => !p.departed)
  const [sel, setSel] = useState<string[]>(() => shown.filter((p) => !dispatchBlock(p)).map((p) => p.id))
  const [carriers, setCarriers] = useState<Ref[] | null>(null)
  const [loads, setLoads] = useState<Load[]>([])
  const [carrierId, setCarrierId] = useState('')
  const [join, setJoin] = useState(NEW)
  const [vehicle, setVehicle] = useState('')
  const [driver, setDriver] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  const today = todayIso()

  const userRef = useRef(user)
  userRef.current = user
  useEffect(() => {
    let live = true
    Promise.all([
      apiJson<LoadOptions>(userRef.current, '/api/dispatches/options'),
      apiJson<{ loads: Load[] }>(userRef.current, '/api/dispatches'),
    ])
      .then(([opts, list]) => {
        if (!live) return
        setCarriers(opts.carriers)
        setLoads(list.loads)
        setCarrierId((c) => c || opts.carriers[0]?.id || '')
      })
      .catch((e) => live && setError(`Failed to load carriers: ${errText(e)}`))
    return () => {
      live = false
    }
  }, [])

  const open = joinableLoads(loads, carrierId, today)
  const target = open.find((l) => l.id === join) || null
  const boxes = shown.filter((p) => sel.includes(p.id)).reduce((a, p) => a + p.boxes, 0)
  const ok = sel.length > 0 && !!carrierId

  const submit = async () => {
    setBusy(true)
    try {
      const r = await apiJson<{ message: string }>(userRef.current, `/api/orders/${orderId}/dispatch`, {
        method: 'POST',
        body: target
          ? { carrier_id: carrierId, load_id: target.id, pallet_ids: sel }
          : { carrier_id: carrierId, planned_date: today, planned_time: nowTime(), vehicle, driver_name: driver, pallet_ids: sel },
      })
      onDone(r.message)
    } catch (e) {
      setError(errText(e))
      setBusy(false)
    }
  }

  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ sel, carrierId, join, vehicle, driver })
  const panel = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, modal: true, busy })
  const label = target ? `Add ${plural(sel.length, 'pallet')} to ${target.reference}` : `Depart ${plural(sel.length, 'pallet')}`

  return (
    <SdPanel
      title="Dispatch pallets"
      sub={orderRef}
      onCancel={panel.requestClose}
      confirm={panel.confirm}
      panelEl={panelEl}
      busy={busy}
      bodyClass={inertClass(busy, slow)}
      foot={
        <PendingButton type="button" className={o.primary} busy={busy} disabled={!ok} pending={target ? 'Adding…' : 'Departing…'} onClick={submit}>
          {label}
        </PendingButton>
      }
    >
      <p className={sd.help}>
        Tick the pallets on the truck. Saving creates the load, verifies the pallets by sight and departs it now: their boxes leave stock and the order
        counts them as dispatched. To share a truck already booked today, add them to that load instead; it departs from its load record.
      </p>
      <div className={sd.grid}>
        <label className={o.field}>
          <span className={o.fieldLabel}>Carrier</span>
          <select
            className={o.input}
            aria-label="Carrier"
            value={carrierId}
            onChange={(e) => {
              setCarrierId(e.target.value)
              setJoin(NEW)
            }}
          >
            {carriers && carriers.length === 0 && <option value="">No carriers: add one in Stock & Dispatch settings</option>}
            {(carriers || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        {open.length > 0 && (
          <label className={o.field}>
            <span className={o.fieldLabel}>Load</span>
            <select className={o.input} aria-label="Load" value={join} onChange={(e) => setJoin(e.target.value)}>
              <option value={NEW}>New load, departs now</option>
              {open.map((l) => (
                <option key={l.id} value={l.id}>
                  {`Add to ${l.reference} · ${l.planned_time || 'no time'} · ${l.state_label} · ${plural(l.pallets, 'pallet')}`}
                </option>
              ))}
            </select>
          </label>
        )}
        {!target && (
          <>
            <label className={o.field}>
              <span className={o.fieldLabel}>Vehicle (optional)</span>
              <input className={o.input} aria-label="Vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)} />
            </label>
            <label className={o.field}>
              <span className={o.fieldLabel}>Driver (optional)</span>
              <input className={o.input} aria-label="Driver" value={driver} onChange={(e) => setDriver(e.target.value)} />
            </label>
          </>
        )}
      </div>
      {error && (
        <div className={styles.errList} role="alert">
          {error}
        </div>
      )}
      <div>
        <div className={styles.strong}>
          {plural(sel.length, 'pallet')} · {plural(boxes, 'box', 'boxes')}
        </div>
        {shown.length === 0 && <p className={sd.muted}>Every pallet on this order has departed.</p>}
        {shown.map((p) => {
          const why = dispatchBlock(p)
          return (
            <label key={p.id} className={ls.candRow} data-testid="dispatch-pallet" style={why ? { opacity: 0.75 } : undefined}>
              <input
                type="checkbox"
                aria-label={p.pallet_id}
                disabled={!!why}
                checked={sel.includes(p.id)}
                onChange={(e) => setSel((s) => (e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id)))}
              />
              <span className={ls.candBody}>
                <span className={styles.strong}>{p.pallet_id}</span>
                <span className={styles.sub}>
                  {p.boxes} boxes · {p.source.variety} · {p.source.box_type}
                  {p.location ? ' · ' + p.location.name : ''}
                </span>
                {why && <span className={ls.gate}>{why}</span>}
              </span>
            </label>
          )
        })}
      </div>
    </SdPanel>
  )
}
